import { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { createControlClient, type ControlClient } from "./api/client.ts";
import { startAuth, type OperatorAuth } from "./auth.ts";
import { loadConfig, type PortalConfig } from "./config.ts";
import { readPortalDocument } from "./session-document.ts";
import type { MenuAccess } from "./ui/access.ts";
import { PortalApp } from "./ui/app.tsx";
import { devChromeSession } from "./ui/chrome.tsx";
import "./ui/styles.css";

type Boot =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; config: PortalConfig; auth: OperatorAuth; client: ControlClient; access?: MenuAccess };

function Root() {
  const [boot, setBoot] = useState<Boot>({ kind: "loading" });
  useEffect(() => {
    void loadConfig()
      .then(async (config) => {
        const fixture = devChromeSession(config);
        if (fixture) {
          setBoot({ kind: "ready", config, auth: fixture.auth, client: fixture.client, access: fixture.access });
          return;
        }
        const auth = await startAuth(config);
        const portalDocument = readPortalDocument(window.location);
        const interactive =
          portalDocument.role === "devices" ||
          portalDocument.role === "recordings" ||
          portalDocument.role === "admin" ||
          portalDocument.role === "stealth";
        const token = interactive ? () => auth.token() : () => auth.token("silent");
        const client = createControlClient({ apiBase: config.apiBase, token });
        setBoot({ kind: "ready", config, auth, client });
      })
      .catch((error: unknown) => {
        setBoot({ kind: "error", message: error instanceof Error ? error.message : "Configuration failed" });
      });
  }, []);
  const view = useMemo(() => boot, [boot]);
  if (view.kind === "loading") return <p className="shell">Loading</p>;
  if (view.kind === "error") return <p className="shell gate">{view.message}</p>;
  return <PortalApp config={view.config} auth={view.auth} client={view.client} access={view.access} />;
}

const root = document.getElementById("root");
if (!root) throw new Error("root element is missing");
createRoot(root).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
